/**
 * The scope picker on the key-creation form: one row per API module, a read box and a
 * write box on each.
 *
 * The rows come from the server (`/account/api-keys/scopes`), so a module added on the
 * API side appears here without a frontend release. That is also why an unlabelled
 * group falls back to its own id rather than rendering an empty row - a new module is
 * readable as "blog" long before anybody writes the translation.
 *
 * Ticking write ticks read with it. A key that may change a post but not read one back
 * is not a choice anybody means to make, and letting it be expressed only produces
 * integrations that half work.
 */
import { Checkbox, Label } from "@wa/ui";
import type { ApiScopeGroup } from "../../../lib/apiKeys";

interface Props {
  groups: ApiScopeGroup[];
  selected: string[];
  onChange: (next: string[]) => void;
  t: (key: string) => string;
}

/** The trailing part of `blog:write` - what the box next to a module is labelled with. */
function action(scope: string): string {
  return scope.split(":")[1] ?? scope;
}

/** A missing translation shows the raw key; fall back to the plain id instead. */
function label(t: (k: string) => string, key: string, fallback: string): string {
  const text = t(key);
  return text === key ? fallback : text;
}

export function ScopePicker({ groups, selected, onChange, t }: Props) {
  function toggle(group: ApiScopeGroup, scope: string, checked: boolean) {
    const next = new Set(selected);
    if (!checked) {
      next.delete(scope);
      // Dropping read drops write with it - write alone cannot read its own result.
      if (action(scope) === "read") for (const s of group.scopes) next.delete(s);
    } else {
      next.add(scope);
      if (action(scope) === "write") {
        const read = group.scopes.find((s) => action(s) === "read");
        if (read) next.add(read);
      }
    }
    onChange([...next]);
  }

  return (
    <div className="space-y-1.5">
      {groups.map((group) => (
        <div
          key={group.id}
          className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-md border px-3 py-2"
        >
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium">
              {label(t, `apiKeys.groups.${group.id}.name`, group.id)}
            </p>
            <p className="text-xs text-muted-foreground">
              {label(t, `apiKeys.groups.${group.id}.hint`, group.scopes.join(", "))}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-4">
            {group.scopes.map((scope) => (
              <Label
                key={scope}
                className="flex cursor-pointer items-center gap-2 text-xs font-normal"
                title={scope}
              >
                <Checkbox
                  checked={selected.includes(scope)}
                  onCheckedChange={(v) => toggle(group, scope, v === true)}
                />
                {t(`apiKeys.action.${action(scope)}`)}
              </Label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
